import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from '../../i18n'
import { useAuth } from '../../contexts/AuthContext'
import { userApi, trainingApi, trainingBlockApi, trainingProgramApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import { workoutApi } from '../../api/workoutApi'
import type { WorkoutSession } from '../../api/workoutApi'
import type {
  TraineeStatistics,
  TrainingSetDetailsResponse,
  TrainingProgramActivation,
  UserRelation,
} from '../../types/api'
import { useTheme } from '../../hooks/useTheme'
import { formatRelativeDate } from '../../utils/dateTranslations'
import SingleTrainingSetWidget from '../training/SingleTrainingSetWidget'
import WaitingForTrainingSetWidget from '../training/WaitingForTrainingSetWidget'
import SoloTrainingWidget from '../training/SoloTrainingWidget'
import ActiveWorkoutWidget from '../training/ActiveWorkoutWidget'
import { WORKOUT_FINISHED_EVENT } from '../../utils/workoutEvents'

export default function TraineeDashboard() {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, translate } = useTranslation()
  const { user } = useAuth()
  const theme = useTheme()

  const [stats, setStats] = useState<TraineeStatistics | null>(null)
  const [loading, setLoading] = useState(true)
  const [trainingSetDetails, setTrainingSetDetails] = useState<TrainingSetDetailsResponse | null>(null)
  const [loadingTrainingSet, setLoadingTrainingSet] = useState(false)
  const [hasSingleTrainingSet, setHasSingleTrainingSet] = useState(false)
  const [activeProgram, setActiveProgram] = useState<TrainingProgramActivation | null>(null)
  const [activeWorkout, setActiveWorkout] = useState<WorkoutSession | null>(null)
  const [coaches, setCoaches] = useState<UserRelation[]>([])
  const [soloLibrary, setSoloLibrary] = useState<{ blockCount: number; programCount: number } | null>(null)

  useEffect(() => {
    loadData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, location.pathname])

  useEffect(() => {
    const refreshActiveWorkout = () => {
      workoutApi.getActiveSession()
        .then((active) => setActiveWorkout(active))
        .catch(() => setActiveWorkout(null))
    }
    window.addEventListener(WORKOUT_FINISHED_EVENT, refreshActiveWorkout)
    return () => window.removeEventListener(WORKOUT_FINISHED_EVENT, refreshActiveWorkout)
  }, [])

  async function loadData() {
    if (!user?.id) return

    setLoading(true)
    try {
      const [statistics, activations, workoutSession, coachesList, soloBlocks, soloPrograms] =
        await Promise.all([
          userApi.getUserStatistics(),
          trainingProgramActivationApi.getHistory().catch(() => [] as TrainingProgramActivation[]),
          workoutApi.getActiveSession().catch(() => null),
          user?.hasCoaches
            ? userApi.getCoaches().catch(() => [] as UserRelation[])
            : Promise.resolve([] as UserRelation[]),
          !user?.hasCoaches
            ? trainingBlockApi.getBlocks().catch(() => [])
            : Promise.resolve([]),
          !user?.hasCoaches
            ? trainingProgramApi.getPrograms().catch(() => [])
            : Promise.resolve([]),
        ])
      setStats(statistics.traineeStats)
      setActiveProgram(activations.find(a => a.isActive) ?? null)
      setActiveWorkout(workoutSession)
      const activeCoaches = coachesList.filter(c => c.relationStatus === 'ACTIVE')
      setCoaches(activeCoaches.length > 0 ? activeCoaches : coachesList)
      if (!user?.hasCoaches) {
        setSoloLibrary({
          blockCount: soloBlocks.length,
          programCount: soloPrograms.length,
        })
      } else {
        setSoloLibrary(null)
      }

      const activeTrainingSetsCount = statistics.traineeStats?.activeTrainingSets ?? 0

      if (activeTrainingSetsCount === 1) {
        setHasSingleTrainingSet(true)
        if (statistics.traineeStats?.closestTrainingSetId) {
          await loadTrainingSetDetails(statistics.traineeStats.closestTrainingSetId)
        } else {
          const allTrainingSets = await trainingApi.getAllTrainingSets()
          const activeTrainingSets = allTrainingSets.filter(
            ts => ts.trainingSet.status === 'ACTIVE' && !ts.isCurrentUserCoach
          )
          if (activeTrainingSets.length === 1) {
            await loadTrainingSetDetails(activeTrainingSets[0].trainingSet.id)
          }
        }
      } else {
        setHasSingleTrainingSet(false)
        setTrainingSetDetails(null)
      }
    } catch {
      setStats(null)
      setHasSingleTrainingSet(false)
      setTrainingSetDetails(null)
      setActiveWorkout(null)
      setCoaches([])
      setSoloLibrary(null)
    } finally {
      setLoading(false)
    }
  }

  async function loadTrainingSetDetails(setId: number) {
    setLoadingTrainingSet(true)
    try {
      const details = await trainingApi.getTrainingSetDetails(setId)
      setTrainingSetDetails(details)
    } catch {
      setTrainingSetDetails(null)
    } finally {
      setLoadingTrainingSet(false)
    }
  }

  const formatNextTraining = (dateTime: string | null): string => {
    if (!dateTime) return ''
    const date = new Date(dateTime)
    const relativeDate = formatRelativeDate(date, t)
    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    return `${relativeDate} ${hours}:${minutes}`
  }

  const nextTrainingLabel = formatNextTraining(stats?.nextTraining ?? null)
  const waitingForSet =
    !!user?.hasCoaches && !hasSingleTrainingSet && (stats?.activeTrainingSets ?? 0) === 0
  const primaryCoach = coaches[0] ?? null

  function currentProgramWeek(startedAt: string): number {
    const elapsed = Math.floor((Date.now() - new Date(startedAt).getTime()) / (7 * 24 * 60 * 60 * 1000))
    return elapsed + 1
  }

  return (
    <>
      {/* Top hero: coach set / waiting, or solo personal training */}
      {waitingForSet && !loading && (
        <WaitingForTrainingSetWidget coach={primaryCoach} coachCount={coaches.length} />
      )}
      {hasSingleTrainingSet && loadingTrainingSet && (
        <div className="mb-6 text-center py-4 text-sm opacity-70">{t.common.loading}</div>
      )}
      {hasSingleTrainingSet && trainingSetDetails && !loadingTrainingSet && (
        <SingleTrainingSetWidget trainingSetDetails={trainingSetDetails} />
      )}
      {!loading && !user?.hasCoaches && (
        <SoloTrainingWidget
          activeProgram={activeProgram}
          activeWorkout={activeWorkout}
          library={soloLibrary}
        />
      )}

      {loading ? (
        <div className="text-center py-10 text-base opacity-70">{t.common.loading}</div>
      ) : (
        <div data-tour="tour-trainee-home">
          {!hasSingleTrainingSet && (stats?.activeTrainingSets ?? 0) > 0 && (
              /* Has 2+ active sets — show next session + stat pills */
              <>
                {stats?.nextTraining && (
                  <div
                    className="rounded-2xl p-4 mb-4 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
                    style={{
                      backgroundColor: theme.cardBgLight,
                      border: `1px solid ${theme.cardBorder}`,
                      boxShadow: theme.shadow.card,
                    }}
                    onClick={() => navigate('/this-week-trainings')}
                  >
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${theme.buttonColor}18` }}
                    >
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.buttonColor || theme.accent.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold" style={{ color: theme.text.primary }}>
                        {t.traineeDashboard.nextSession.title}
                      </div>
                      <div className="text-xs mt-0.5" style={{ color: theme.text.muted }}>
                        {nextTrainingLabel}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rounded-xl px-3 py-1.5 text-xs font-bold flex-shrink-0"
                      style={{ background: theme.buttonColor, color: theme.buttonTextColor || '#ffffff' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate('/this-week-trainings')
                      }}
                    >
                      {t.traineeDashboard.nextSession.view}
                    </button>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3 mb-5">
                  <div
                    className="rounded-2xl p-4"
                    style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
                  >
                    <div
                      className="text-3xl font-extrabold leading-none mb-1"
                      style={{ letterSpacing: '-1px', color: theme.text.primary }}
                    >
                      {stats?.thisWeekTrainings ?? 0}
                    </div>
                    <div className="text-xs font-medium" style={{ color: theme.text.muted }}>
                      {t.traineeDashboard.statsWeek}
                    </div>
                  </div>
                  <div
                    className="rounded-2xl p-4"
                    style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
                  >
                    <div
                      className="text-3xl font-extrabold leading-none mb-1"
                      style={{ letterSpacing: '-1px', color: theme.text.primary }}
                    >
                      {stats?.activeTrainingSets ?? 0}
                    </div>
                    <div className="text-xs font-medium" style={{ color: theme.text.muted }}>
                      {t.traineeDashboard.statsCoaches}
                    </div>
                  </div>
                </div>
              </>
          )}

          {/* Manage trainees link — for users who are also coaches */}
          {user?.hasTrainees && (
            <button
              className="w-full mb-4 rounded-2xl p-4 flex items-center gap-3 cursor-pointer active:scale-[0.98] transition-transform"
              style={{ backgroundColor: theme.cardBgLight, border: `1px solid ${theme.cardBorder}` }}
              onClick={() => navigate('/trainees')}
            >
              <span className="text-xl flex-shrink-0">👥</span>
              <span className="flex-1 text-sm font-semibold text-left" style={{ color: theme.text.primary }}>
                {t.traineeDashboard.manageTrainees.title}
              </span>
            </button>
          )}

          {/* Personal program / workout when training with a coach (solo uses SoloTrainingWidget) */}
          {!!user?.hasCoaches && (
            activeWorkout?.id != null ? (
            <ActiveWorkoutWidget workout={activeWorkout} />
          ) : activeProgram && (() => {
            const week = Math.min(currentProgramWeek(activeProgram.startedAt), activeProgram.durationWeeks)
            const progress = week / activeProgram.durationWeeks
            return (
              <div
                className="mb-5 rounded-2xl p-4 cursor-pointer active:scale-[0.98] transition-transform"
                style={{
                  backgroundColor: theme.cardBgLight,
                  border: '1px solid rgba(0,136,204,0.3)',
                  boxShadow: theme.shadow.card,
                }}
                onClick={() => navigate(`/training-programs/${activeProgram.programId}`)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-lg"
                    style={{ background: 'rgba(0,136,204,0.12)' }}
                  >
                    🗂️
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold mb-0.5" style={{ color: 'rgba(0,136,204,1)' }}>
                      {t.traineeDashboard.activeProgram.title}
                    </div>
                    <div className="text-sm font-bold truncate" style={{ color: theme.text.primary }}>
                      {activeProgram.programName}
                    </div>
                  </div>
                  <div
                    className="rounded-lg px-2.5 py-1 text-xs font-bold flex-shrink-0"
                    style={{ background: 'rgba(0,136,204,0.12)', color: 'rgba(0,136,204,1)' }}
                  >
                    {translate('traineeDashboard.activeProgram.weekOf', { current: week, total: activeProgram.durationWeeks })}
                  </div>
                </div>
                <div className="rounded-full overflow-hidden h-1.5" style={{ backgroundColor: 'rgba(0,136,204,0.12)' }}>
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.round(progress * 100)}%`, background: 'rgba(0,136,204,1)' }}
                  />
                </div>
                <button
                  type="button"
                  className="mt-3 w-full rounded-xl py-2.5 text-sm font-semibold active:scale-[0.98] transition-transform"
                  style={{ background: 'rgba(0,136,204,1)', color: '#fff' }}
                  onClick={(e) => {
                    e.stopPropagation()
                    navigate(`/training-programs/${activeProgram.programId}`)
                  }}
                >
                  {t.workout.startTitle}
                </button>
              </div>
            )
          })()
          )}

        </div>
      )}
    </>
  )
}
